import { useModalStore } from "@/hooks/useModalStore";
import { Modal, Input, Button, Form, message } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DeleteIcon, PlusIcon } from "@/components/icons";
import AudienceSample from "@/components/AudienceSample";
import NotificationSuccess from "@/components/NotificationSuccess";
import { updateMatchInfoApi } from "@/api/createCampaignApi";

const { TextArea } = Input;

export default function MatchInfluencerAdjustModal() {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { matchInfluencerAdjustModalInfo, setMatchInfluencerAdjustModalInfo } =
    useModalStore();

  const [loading, setLoading] = useState(false);
  const onSubmit = () => {
    form
      .validateFields()
      .then(async (values) => {
        setLoading(true);
        try {
          await updateMatchInfoApi(
            matchInfluencerAdjustModalInfo?.campaignId,
            values
          );
          handleClosePorduct();
          NotificationSuccess({
            message: t('components.matchInfluencerAdjustModal.successMessage'),
            width: 450,
          });
          matchInfluencerAdjustModalInfo?.updateFunc &&
            matchInfluencerAdjustModalInfo?.updateFunc();
        } catch (error: any) {
          message.error(`${error?.message}`);
        } finally {
          setLoading(false);
        }
      })
      .catch((error: any) => {
        message.error(`${error?.errorFields?.[0]?.errors?.[0]}`);
      });
  };

  const handleClosePorduct = () => {
    setMatchInfluencerAdjustModalInfo({
      ...matchInfluencerAdjustModalInfo,
      campaignId: "",
      visible: false,
    });
  };

  useEffect(() => {
    if (matchInfluencerAdjustModalInfo?.visible) {
      form.setFieldsValue({
        intro: matchInfluencerAdjustModalInfo?.intro,
        coreFeature: matchInfluencerAdjustModalInfo?.coreFeature,
        coreAudience: matchInfluencerAdjustModalInfo?.coreAudience,
      });
    }
  }, [
    matchInfluencerAdjustModalInfo?.visible,
    matchInfluencerAdjustModalInfo?.intro,
    matchInfluencerAdjustModalInfo?.coreFeature,
    matchInfluencerAdjustModalInfo?.coreAudience,
    form,
  ]);

  return (
    <>
      <Modal
        open={matchInfluencerAdjustModalInfo?.visible}
        title={t('components.matchInfluencerAdjustModal.title')}
        footer={null}
        onCancel={handleClosePorduct}
        width={993}
      >
        <div className="self-stretch justify-start text-text-color-secondary text-xs font-normal font-['DM_Sans'] leading-tight">
          {t('components.matchInfluencerAdjustModal.description')}
        </div>
        <div className="border-t border-divideColor w-full my-4"></div>
        <div className="h-[60vh] overflow-y-auto">
          <Form form={form}>
            <div className="flex flex-col gap-4">
              <div className="self-stretch justify-start text-text-color-primary text-xl font-semibold font-['Plus_Jakarta_Sans'] leading-loose">
                {t('components.matchInfluencerAdjustModal.businessIntroduction')}
              </div>
              <Form.Item
                name="intro"
                rules={[
                  {
                    required: true,
                    message: t('components.matchInfluencerAdjustModal.introRequired'),
                  },
                ]}
              >
                <TextArea
                  placeholder={t('components.matchInfluencerAdjustModal.introPlaceholder')}
                  autoSize={{ minRows: 3, maxRows: 5 }}
                />
              </Form.Item>
            </div>
            <div className="flex flex-col gap-4">
              <div className="self-stretch justify-start text-text-color-primary text-xl font-semibold font-['Plus_Jakarta_Sans'] leading-loose">
                {t('components.matchInfluencerAdjustModal.coreSellingPoints')}
              </div>
              <Form.List name="coreFeature">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field, index) => (
                      <div className="flex items-center gap-2" key={field.key}>
                        <span className="h-8 mb-6 text-[#09090B] flex items-center">
                          {index + 1}.{" "}
                        </span>
                        <Form.Item
                          key={field.key}
                          name={[field.name]}
                          rules={[
                            {
                              required: true,
                              message: t('components.matchInfluencerAdjustModal.coreFeatureRequired'),
                            },
                          ]}
                          className="w-full"
                        >
                          <TextArea autoSize={{ minRows: 1, maxRows: 5 }} />
                        </Form.Item>
                        {fields?.length > 1 && (
                          <span
                            className="cursor-pointer mb-[24px]"
                            onClick={() => remove(field.name)}
                          >
                            <DeleteIcon className="delete" />
                          </span>
                        )}
                      </div>
                    ))}

                    <Form.Item className="mb-0">
                      <div
                        className="flex items-center text-[#FB6011] cursor-pointer font-medium"
                        onClick={() => add("")}
                      >
                        <PlusIcon className="text-[#FB6011]" />
                        <span>{t('components.matchInfluencerAdjustModal.add')}</span>
                      </div>
                    </Form.Item>
                  </>
                )}
              </Form.List>
            </div>
            <div className="flex flex-col gap-4">
              <div className="text-[#1c2024] text-[23px] font-semibold font-['Plus Jakarta Sans'] leading-9 mb-6 flex items-center justify-between">
                {t('components.matchInfluencerAdjustModal.coreAudiences')}
                <AudienceSample />
              </div>
              <Form.List name="coreAudience">
                {(fields, { add, remove }) => (
                  <div className="flex flex-col gap-6">
                    {fields.map((field, index) => (
                      <div
                        key={field.key}
                        className="flex items-center gap-2 w-full"
                      >
                        <div className="flex flex-col gap-2 border border-[#E4E4E7] rounded-md py-2 px-3 flex-1">
                          <div className="flex flex-col gap-1">
                            <div className="text-[#A3A3A3] font-[12px]">
                              {t('components.matchInfluencerAdjustModal.titleLabel')}
                            </div>
                            <Form.Item
                              name={[field.name, "audience_name"]}
                              rules={[
                                {
                                  required: true,
                                  message: t('components.matchInfluencerAdjustModal.audienceTitleRequired'),
                                },
                              ]}
                            >
                              <Input placeholder={t('components.matchInfluencerAdjustModal.titlePlaceholder')} />
                            </Form.Item>
                          </div>
                          <div className="">
                            <div className="text-[#A3A3A3] font-[12px]">
                              {t('components.matchInfluencerAdjustModal.descriptionLabel')}
                            </div>
                            <Form.Item
                              name={[field.name, "audience_description"]}
                              rules={[
                                {
                                  required: true,
                                  message: t('components.matchInfluencerAdjustModal.audienceDescriptionRequired'),
                                },
                              ]}
                            >
                              <TextArea
                                autoSize={{ minRows: 1, maxRows: 5 }}
                                placeholder={t('components.matchInfluencerAdjustModal.descriptionPlaceholder')}
                              />
                            </Form.Item>
                          </div>
                        </div>
                        {fields?.length > 1 && (
                          <span
                            className="cursor-pointer"
                            onClick={() => remove(field.name)}
                          >
                            <DeleteIcon className="delete" />
                          </span>
                        )}
                      </div>
                    ))}
                    <Form.Item className="mb-0">
                      <div
                        className="flex items-center text-[#FB6011] cursor-pointer font-medium"
                        onClick={() => add("")}
                      >
                        <PlusIcon className="text-[#FB6011]" />
                        <span>{t('components.matchInfluencerAdjustModal.add')}</span>
                      </div>
                    </Form.Item>
                  </div>
                )}
              </Form.List>
            </div>
          </Form>
        </div>
        <div className="flex items-center pt-4 justify-end gap-2">
          <Button size="large" onClick={handleClosePorduct}>
            {t('components.matchInfluencerAdjustModal.cancel')}
          </Button>
          <Button
            size="large"
            type="primary"
            onClick={onSubmit}
            loading={loading}
          >
            {t('components.matchInfluencerAdjustModal.updateButton')}
          </Button>
        </div>
      </Modal>
    </>
  );
}
